import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridTable, Pagination } from 'components/common';
import { getProjectScheduleOrderEntry } from 'redux/crm/report/reportSlice';
import { COLUMN_DEFS } from './Column';
import useResizableHeight from './useResizableHeight';
import ResizeHandle from './ResizeHandle';

const DEFAULT_SORT = { attribute: 'id', direction: 'desc' };

// Headers wrap instead of truncating — "Quote N…" / "P/J Nu…" are unreadable,
// and this grid has 37 columns so widening them is not an option.
const COL_DEF_OVERRIDE = { wrapHeaderText: true, autoHeaderHeight: true };

/** The original change-order grid: one row per CO, paginated server-side. */
const DetailTab = ({ active, filterParams, filterKey, reloadToken, onTotalsChange }) => {
  const dispatch = useDispatch();
  const { projectScheduleData: { data = [], totalRows = 0, totalPagesCount = 1 } } = useSelector((s) => s.crm_report);

  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort]       = useState(DEFAULT_SORT);
  const [loading, setLoading] = useState(false);
  const { height, min, max, startResize, onKeyDown } = useResizableHeight({ initial: 520, min: 280, max: 1000 });

  // Filter changes restart at page 1; without this a narrowed result set would
  // land the user on an empty page 7.
  const lastFilterKey = useRef(filterKey);
  useEffect(() => {
    if (lastFilterKey.current === filterKey) return;
    lastFilterKey.current = filterKey;
    setPage(1);
  }, [filterKey]);

  const fetchKey = `${reloadToken}|${filterKey}|${page}|${perPage}|${sort.attribute}|${sort.direction}`;
  const fetchedKey = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch(getProjectScheduleOrderEntry({ ...filterParams, page, per_page: perPage, sort }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, filterParams, page, perPage, sort]);

  useEffect(() => {
    if (!active || fetchedKey.current === fetchKey) return;
    fetchedKey.current = fetchKey;
    fetchData();
  }, [active, fetchKey, fetchData]);

  useEffect(() => {
    if (!active) return;
    onTotalsChange(`${totalRows.toLocaleString()} records`);
  }, [active, totalRows, onTotalsChange]);

  const handleSort = useCallback(({ colId, sort: direction }) => {
    // colId doubles as the Ransack sort key — see the comment in Column.js.
    setSort(direction ? { attribute: colId, direction } : DEFAULT_SORT);
    setPage(1);
  }, []);

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <div className="tw-flex tw-flex-col">
        <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-shadow-sm">
          <AgGridTable
            rowData={data}
            columnDefs={COLUMN_DEFS}
            loading={loading}
            pagination={false}
            filter={false}
            height={`${height}px`}
            rowHeight={34}
            headerHeight={40}
            fontSize="12px"
            enableCellTextSelection
            colDefOverride={COL_DEF_OVERRIDE}
            onSortChanged={handleSort}
          />
        </div>

        <ResizeHandle
          label="Resize project schedule table"
          height={height}
          min={min}
          max={max}
          onPointerDown={startResize}
          onKeyDown={onKeyDown}
        />
      </div>

      <Pagination
        currentPage={page}
        totalPages={totalPagesCount || Math.ceil(totalRows / perPage) || 1}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPerPage(size); setPage(1); }}
        disabled={loading}
      />
    </div>
  );
};

DetailTab.propTypes = {
  active:         PropTypes.bool.isRequired,
  filterParams:   PropTypes.object.isRequired,
  filterKey:      PropTypes.string.isRequired,
  reloadToken:    PropTypes.number.isRequired,
  onTotalsChange: PropTypes.func.isRequired,
};

export default DetailTab;
